import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { audit, type AuditFilters } from '../api/client';
import { Badge, Button, ErrorState, Field, Input, Select, Spinner } from './ui';
import { formatDateTime } from '../lib/format';

const PAGE_SIZE = 25;

/**
 * Settings → Audit (TASKS.md B6). ADMIN only: the trail names who did what, so
 * it is read-only for operators. Newest first, filterable, paginated.
 *
 * A row's `action` is either a named event (`login`, `login_denied`, `logout`,
 * `credential_viewed`, `user_created` …) or a mechanical mutation
 * (`post_subscription`, `patch_payment`, `delete_money_account`, …); the detail
 * column carries what the api could say without leaking a value.
 */
export function AuditPanel() {
  const [filters, setFilters] = useState<AuditFilters>({ page: 1, limit: PAGE_SIZE });

  const actions = useQuery({
    queryKey: ['audit', 'actions'],
    queryFn: ({ signal }) => audit.actions(signal),
    staleTime: 300_000,
  });

  const list = useQuery({
    queryKey: ['audit', 'list', filters],
    queryFn: ({ signal }) => audit.list(filters, signal),
    placeholderData: keepPreviousData,
  });

  const set = (patch: Partial<AuditFilters>) => setFilters((current) => ({ ...current, page: 1, ...patch }));

  const items = list.data?.items ?? [];
  const meta = list.data?.meta;

  return (
    <div className="card">
      <div className="page-header" style={{ marginBottom: 'var(--space-3)' }}>
        <h2 className="card__title" style={{ margin: 0 }}>Audit trail</h2>
        {meta ? (
          <span className="cell-muted">
            {meta.total} entr{meta.total === 1 ? 'y' : 'ies'}
          </span>
        ) : null}
      </div>

      <div className="toolbar" style={{ gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <Field label="User" htmlFor="audit-user">
          <Input
            id="audit-user"
            placeholder="e-mail contains…"
            value={filters.user ?? ''}
            onChange={(event) => set({ user: event.target.value || undefined })}
          />
        </Field>
        <Field label="Action" htmlFor="audit-action">
          <Select
            id="audit-action"
            value={filters.action ?? ''}
            onChange={(event) => set({ action: event.target.value || undefined })}
          >
            <option value="">any</option>
            {(actions.data ?? []).map((value) => (
              <option key={value} value={value}>{value}</option>
            ))}
          </Select>
        </Field>
        <Field label="Entity" htmlFor="audit-entity">
          <Input
            id="audit-entity"
            placeholder="subscription, payment…"
            value={filters.entityType ?? ''}
            onChange={(event) => set({ entityType: event.target.value || undefined })}
          />
        </Field>
        <Field label="From" htmlFor="audit-from">
          <Input
            id="audit-from"
            type="date"
            value={filters.from ?? ''}
            onChange={(event) => set({ from: event.target.value || undefined })}
          />
        </Field>
        <Field label="To" htmlFor="audit-to">
          <Input
            id="audit-to"
            type="date"
            value={filters.to ?? ''}
            onChange={(event) => set({ to: event.target.value || undefined })}
          />
        </Field>
        {Object.values(filters).some((value) => value !== undefined && value !== 1 && value !== PAGE_SIZE) ? (
          <div style={{ alignSelf: 'flex-end' }}>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFilters({ page: 1, limit: PAGE_SIZE })}
            >
              Reset
            </Button>
          </div>
        ) : null}
      </div>

      {list.isPending ? (
        <Spinner label="Loading the trail" />
      ) : list.isError ? (
        <ErrorState message={list.error.message} onRetry={() => void list.refetch()} />
      ) : items.length === 0 ? (
        <p className="cell-muted">Nothing matches these filters.</p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th scope="col">When</th>
                <th scope="col">User</th>
                <th scope="col">Action</th>
                <th scope="col">Entity</th>
                <th scope="col">Detail</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id}>
                  <td className="cell-muted" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.createdAt)}</td>
                  <td>
                    <span className="cell-clip" title={row.userEmail}>{row.userEmail}</span>
                  </td>
                  <td>
                    <Badge tone={toneFor(row.action)} title={row.subscriptionId ?? undefined}>{row.action}</Badge>
                  </td>
                  <td className="cell-muted">
                    {row.entityType ? `${row.entityType}${row.entityId ? ` · ${short(row.entityId)}` : ''}` : '—'}
                  </td>
                  <td className="cell-muted">
                    <span className="cell-clip" title={row.detail ? JSON.stringify(row.detail) : undefined}>
                      {row.detail ? JSON.stringify(row.detail) : '—'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pagination">
        <span>
          Page {meta?.page ?? filters.page ?? 1} of {meta?.totalPages ?? 1}
        </span>
        <div className="pagination__pages">
          <Button
            size="sm"
            variant="secondary"
            disabled={(meta?.page ?? 1) <= 1}
            onClick={() => setFilters((current) => ({ ...current, page: (current.page ?? 1) - 1 }))}
          >
            Previous
          </Button>
          <Button
            size="sm"
            variant="secondary"
            disabled={(meta?.page ?? 1) >= (meta?.totalPages ?? 1)}
            onClick={() => setFilters((current) => ({ ...current, page: (current.page ?? 1) + 1 }))}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Cuids are 25 chars; the tail is what an operator compares between two rows. */
function short(id: string): string {
  return id.length > 10 ? `…${id.slice(-8)}` : id;
}

/** Logins and refusals read as warnings; ordinary mutations stay neutral. */
function toneFor(action: string): 'warn' | 'bad' | 'neutral' {
  if (action === 'login_denied') return 'bad';
  if (action === 'login' || action === 'logout' || action === 'credential_viewed') return 'warn';
  return 'neutral';
}
